import { Trans } from '@lingui/macro'
import Web3Status from 'components/Web3Status'
import { useIsPoolsPage } from 'hooks/useIsPoolsPage'
import { Box } from 'nft/components/Box'
import { Row } from 'nft/components/Flex'
import {NavBarBurgerIcon} from 'nft/components/icons'
import {ReactNode, useRef, useState} from 'react'
import { NavLink, NavLinkProps, useLocation, useNavigate } from 'react-router-dom'
import {Text} from "rebass";
import styled from 'styled-components/macro'
import { BREAKPOINTS } from 'theme';
import { ReactComponent as CratLogo } from 'assets/svg/crat-logo-animated.svg'

import {useOnClickOutside} from "../../hooks/useOnClickOutside";
import {vars} from "../../nft/css/sprinkles.css";
import Column from "../Column";
import { SearchBar } from './SearchBar'
import * as styles from './style.css'

const Nav = styled.nav`
  padding: 16px 60px;
  width: 100%;
  height: ${({ theme }) => theme.navHeight}px;
  z-index: 2;
  @media (max-width: ${BREAKPOINTS.md}px) {
    padding: 16px;
    background: ${({ theme }) => theme.backgroundSurface};
    backdrop-filter: blur(30px);
  }
`

const StyledMenu = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  border: none;
  text-align: left;
  @media (max-width: ${BREAKPOINTS.md}px) {
    display: block;
  }
`

const MenuFlyout = styled.span`
  padding: 8px;
  width: 148px;
  background-color: ${({ theme }) => theme.backgroundSurface};
  backdrop-filter: blur(30px);
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.01), 0 4px 8px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.04),
  0 24px 32px rgba(0, 0, 0, 0.01);
  border: 1px solid ${({ theme }) => theme.borderPrimary};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  position: absolute;
  top: 2rem;
  left: 0;
  z-index: 100;
  color: ${({ theme }) => theme.textPrimary};
  transition: all 0.5s ease-out;
  user-select: none;
  div > a, .pool-dropdown-trigger {
    background: ${vars.color.lightGrayOverlay};
  }
`

interface MenuItemProps {
  href: string
  id?: NavLinkProps['id']
  isActive?: boolean
  children: ReactNode
  onClick?: () => void
}

const MenuItem = ({ href, id, isActive, children, onClick }: MenuItemProps) => {
  return (
    <NavLink
      to={href}
      onClick={onClick}
      className={isActive ? styles.activeMenuItem : styles.menuItem}
      id={id}
      style={{ textDecoration: 'none' }}
    >
      {children}
    </NavLink>
  )
}

export const PageTabs = ({ onClick }: { onClick?: () => void }) => {
  const poolsNavRef = useRef<HTMLDivElement | null>(null)
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false)
  const { pathname } = useLocation()

  const isPoolActive = useIsPoolsPage()

  const onCloseNavDropdown = () => {
    setIsNavDropdownOpen(false)
    onClick?.()
  }

  useOnClickOutside(poolsNavRef, isNavDropdownOpen ? onCloseNavDropdown : undefined)

  return (
    <>
      <MenuItem href="/" isActive={pathname === '/'} onClick={onClick}>
        <Trans>Swap</Trans>
      </MenuItem>
      <MenuItem href="/tokens" isActive={pathname.startsWith('/tokens')} onClick={onClick}>
        <Trans>Tokens</Trans>
      </MenuItem>
      <Box className="pool-dropdown-trigger" ref={poolsNavRef} position="relative" cursor="pointer" style={{ borderRadius: '8px', margin: '4px 0' }}>
        <Text className={isPoolActive ? styles.activeMenuItem : styles.menuItem} onClick={() => setIsNavDropdownOpen(prev => !prev)}>
          <Trans>Pools</Trans>
        </Text>
        {isNavDropdownOpen && (
          <MenuFlyout style={{ top: '3rem' }}>
            <Column>
              <MenuItem href="/pools" onClick={onCloseNavDropdown}>
                View position
              </MenuItem>
              <MenuItem href="/add" onClick={onCloseNavDropdown}>
                Create position
              </MenuItem>
            </Column>
          </MenuFlyout>
        )}
      </Box>
    </>
  )
}

const NavbarMobile = () => {
  const [isNavBarOpen, setIsNavBarOpen] = useState(false)
  const navbarRef = useRef<HTMLDivElement | null>(null)

  const onCloseNavBar = () => setIsNavBarOpen(false)

  useOnClickOutside(navbarRef, onCloseNavBar)

  return (
    <StyledMenu ref={navbarRef}>
      <NavBarBurgerIcon onClick={() => setIsNavBarOpen(prev => !prev)} />
      {isNavBarOpen && (
        <MenuFlyout>
          <Column>
            <PageTabs onClick={onCloseNavBar} />
          </Column>
        </MenuFlyout>
      )}
    </StyledMenu>
  )
}

const Navbar = () => {
  const navigate = useNavigate()

  return (
    <>
      <Nav>
        <Box display="flex" height="full" flexWrap="nowrap">
          <Box className={styles.leftSideContainer}>
            <Box className={styles.logoContainer}>
              <Box
                data-testid="cratswap-logo"
                className={styles.logo}
                onClick={() => {
                  navigate({
                    pathname: '/',
                  })
                }}
              >
                <CratLogo />
                <Text className={styles.logoTitle}>CratSwap</Text>
              </Box>
            </Box>
            <Row display={{ sm: 'none', lg: 'flex' }}>
              <PageTabs />
            </Row>
            <NavbarMobile />
          </Box>
          <Box className={styles.searchContainer}>
            <SearchBar />
          </Box>
          <Box className={styles.rightSideContainer}>
            <Row gap="12">
              <Box position="relative" display={{ sm: 'flex', navSearchInputVisible: 'none' }}>
                <SearchBar />
              </Box>
              <Web3Status />
            </Row>
          </Box>
        </Box>
      </Nav>
    </>
  )
}

export default Navbar
